/**
 * Service-worker registration with update handling.
 *
 * The SW lives at /public/sw.js and is shipped to the document root. We
 * register it after `load` so we don't compete with first paint, and we
 * watch for new versions via the `updatefound` lifecycle so the user gets
 * a chance to refresh into the new build.
 */

const isLocalhost = () =>
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1" ||
    window.location.hostname.endsWith(".local"));

let _registration = null;
let _updateListeners = [];

export function getRegistration() {
  return _registration;
}

export function onUpdateAvailable(listener) {
  _updateListeners.push(listener);
  return () => {
    _updateListeners = _updateListeners.filter((l) => l !== listener);
  };
}

function notifyUpdateAvailable(registration) {
  for (const listener of _updateListeners) {
    try { listener(registration); } catch { /* swallow */ }
  }
}

export function registerServiceWorker() {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return;
  }

  // Don't register on localhost by default — caching the dev SPA shell makes
  // hot-reload misbehave. Override with VITE_SW_DEV=1 for testing.
  if (isLocalhost() && import.meta.env.VITE_SW_DEV !== "1") {
    return;
  }

  window.addEventListener("load", async () => {
    try {
      const registration = await navigator.serviceWorker.register("/sw.js");
      _registration = registration;

      // Watch for an update.
      registration.addEventListener("updatefound", () => {
        const installing = registration.installing;
        if (!installing) return;

        installing.addEventListener("statechange", () => {
          if (
            installing.state === "installed" &&
            navigator.serviceWorker.controller
          ) {
            // A new SW is waiting to take over — let listeners decide what to do.
            notifyUpdateAvailable(registration);
          }
        });
      });

      // When the new SW takes over, reload to pick up the new bundle.
      let refreshing = false;
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        if (refreshing) return;
        refreshing = true;
        window.location.reload();
      });
    } catch (err) {
      // Don't surface to the user — the app works fine without the SW.
      if (import.meta.env.DEV) {
        console.warn("[sw] registration failed:", err);
      }
    }
  });
}

/**
 * Tell the waiting SW to skipWaiting and become the active worker.
 * Call this after the user accepts an "Update available" prompt.
 */
export function applyUpdate() {
  if (_registration?.waiting) {
    _registration.waiting.postMessage({ type: "SKIP_WAITING" });
  }
}
